import {
  buildCityPageUrl,
  buildCppSubpageUrl,
  buildIndustryPageUrl,
  buildMajorSubPageUrl,
  getStateName,
} from '../../utils';
import { FILTER_PARAMS,filterSearchKey } from '../zpFilter/filterUtils';

/**
 * This will be the Type of chart
 */
export const CHART_TYPES = {
  TYPE_A: 'TYPE_A',
  TYPE_B: 'TYPE_B',
  TYPE_C: 'TYPE_C',
};

/**
 * This will be the Page name from where we want to create salary chart
 */
export const PAGE = {
  JOB_TITLE: 'jobTitle',
  COMPANY: 'company',
  MAJOR: 'major',
};

/**
 * This will be the Entity of the list items for a specific page
 */
export const ENTITY = {
  JOB_TITLE: 'jobTitle',
  LOCATION: 'location',
  INDUSTRY: 'industry',
  DEPARTMENT: 'department',
  MAJOR: 'major',
  COMPANY: 'company',
};

const getLinkUrl = (
  entity,
  page,
  salaryChartItem,
  displayFlagOfCompanyJobsPage,
  companyLink
) => {
  switch (entity) {
    case ENTITY.LOCATION:
      switch (page) {
        case PAGE.JOB_TITLE:
          // Combo Salary Page Link
          return salaryChartItem.comboSalaryLink;
        case PAGE.COMPANY:
          return displayFlagOfCompanyJobsPage ? buildCppSubpageUrl(companyLink, '', 'jobs') : '';
        case PAGE.MAJOR:
          return buildCityPageUrl(
            salaryChartItem.city,
            salaryChartItem.state || salaryChartItem.stateCode
          );
        default:
          return '';
      }
    case ENTITY.JOB_TITLE:
      if (displayFlagOfCompanyJobsPage && companyLink) {
        return buildCppSubpageUrl(companyLink, '', 'jobs');
      } else {
        return '';
      }
  }
};

const isLinkIndexed = (entity, page, salaryChartItem, displayFlagOfCompanyJobsPage, department) => {
  if (entity === ENTITY.LOCATION) {
    switch (page) {
      case PAGE.JOB_TITLE:
        return salaryChartItem.comboSalaryPageIndexable;
      case PAGE.COMPANY:
        return displayFlagOfCompanyJobsPage;
      case PAGE.MAJOR:
        return true;
      default:
        return false;
    }
  } else if (entity === ENTITY.JOB_TITLE) {
    return department ? salaryChartItem.isSalaryPageIndexed : displayFlagOfCompanyJobsPage;
  }
};

const getOnLinkClick = (page, entity, entityValue, department) => {
  if (page !== PAGE.COMPANY || department) {
    return;
  }

  let entityKey = '';

  if (entity === ENTITY.LOCATION) {
    entityKey = FILTER_PARAMS.LOCATION;
  } else if (entity === ENTITY.JOB_TITLE) {
    entityKey = FILTER_PARAMS.JOB_TITLE;
  }

  if (entityKey) {
    window.localStorage.setItem(
      filterSearchKey,
      JSON.stringify({
        [entityKey]: [entityValue],
      })
    );
  }
};

/**
 * Maps List of Salary Items according to the New Salary Chart component format (Only Type A)
 * @param {String} type Type from CHART_TYPES
 * @param {Array<Object>} salaryChartItems List of Salary Items
 * @param {String} entity Entity from ENTITY
 * @param {String} page Page from PAGE
 * @param {Boolean} displayFlagOfCompanyJobsPage Only required for Company Page => By Job Title section AND By Location sections
 * @param {String} companyLink Only required for Company Page => By Job Title section AND By Location sections
 * @param {String} department Only required for Comapany Individual departments
 */
export const mapSalaryChartItemsForTypeA = (
  salaryChartItems = [],
  entity,
  page,
  displayFlagOfCompanyJobsPage = false,
  companyLink = '',
  department = '',
  forceIndex = false
) => {
  switch (entity) {
    case ENTITY.LOCATION:
      return salaryChartItems.map((salaryChartItem) => {
        const { city, state, stateCode, median, ...rest } = salaryChartItem;
        return {
          title: city ? `${city}, ${stateCode}` : getStateName(stateCode),
          link: getLinkUrl(
            entity,
            page,
            salaryChartItem,
            displayFlagOfCompanyJobsPage,
            companyLink
          ),
          median,
          city,
          state,
          stateCode,
          isLinkIndexed: isLinkIndexed(entity, page, rest, displayFlagOfCompanyJobsPage),
          onLinkClick: () => getOnLinkClick(page, entity, { city, state: stateCode || state }),
          ...rest,
        };
      });
    case ENTITY.INDUSTRY:
      return salaryChartItems.map(({ industry, median, ...rest }) => ({
        title: industry,
        link: forceIndex && industry ? buildIndustryPageUrl(industry) : '',
        median,
        isLinkIndexed: forceIndex,
        ...rest,
      }));
    case ENTITY.DEPARTMENT:
      return salaryChartItems.map(({ department, median, ...rest }) => ({
        title: department,
        link: '',
        median,
        isLinkIndexed: false,
        ...rest,
      }));
    case ENTITY.JOB_TITLE:
      return salaryChartItems.map((salaryChartItem) => {
        const { jobTitle, median, ...rest } = salaryChartItem;
        return {
          title: jobTitle,
          link: getLinkUrl(
            entity,
            page,
            salaryChartItem,
            displayFlagOfCompanyJobsPage,
            companyLink,
            department
          ),
          median,
          isLinkIndexed: isLinkIndexed(
            entity,
            page,
            salaryChartItem,
            displayFlagOfCompanyJobsPage,
            department
          ),
          onLinkClick: () => getOnLinkClick(page, entity, jobTitle, department),
          ...rest,
        };
      });
    case ENTITY.MAJOR:
      return salaryChartItems.map(
        ({ major, median, majorSalaryPageLink, majorSalaryPageIndexed, ...rest }) => ({
          title: major,
          link: majorSalaryPageLink || buildMajorSubPageUrl(major, 'salary'),
          median,
          isLinkIndexed: majorSalaryPageIndexed,
          ...rest,
        })
      );
  }
};
